import { Avatar, Box, Skeleton, SxProps, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ToastType, useToast } from 'services/toast';
import { S3Bucket } from 'core/services/amplify/config/constants';
import { useAmplifyUploadData } from 'core/services/amplify/storage/useAmplifyUploadFile';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useAmplifyFileURL } from 'core/services/amplify/storage/useAmplifyFileURL';

const avatarContainer: SxProps = {
  marginRight: 4
};

type Props = {
  size?: number;
};

export const OrganizationLogo: React.FC<Props> = ({ size = 122 }) => {
  const organization = useOrganization();
  const isAdmin = useIsAdmin();
  const theme = useTheme();

  const { showToast } = useToast();
  const [file, setFile] = useState<File | undefined>(undefined);

  const { uploadData } = useAmplifyUploadData({
    bucket: S3Bucket.PUBLIC,
    prefix: 'organization_logos',
    customFileName: organization.id
  });

  const { url } = useAmplifyFileURL({
    bucket: S3Bucket.PUBLIC,
    path: organization?.logoS3Key
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }
      await uploadData({ file });
      showToast({
        message: 'Logo updated successfully',
        type: ToastType.Success
      });
      setFile(file);
    },
    [uploadData]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'image/*': [] }
  });

  if (!organization) {
    return <Skeleton variant="circular" sx={avatarContainer} />;
  }

  return (
    <Box
      {...(isAdmin ? getRootProps() : {})}
      sx={{
        position: 'relative',
        cursor: isAdmin ? 'pointer' : 'default'
      }}
    >
      <Avatar
        sx={{
          ...avatarContainer,
          height: size,
          width: size,
          border: '3px solid transparent',
          '&:hover': isAdmin
            ? {
                border: `3px solid ${theme.palette.primary.main}`
              }
            : {}
        }}
        src={(file && URL.createObjectURL(file)) || url?.href || undefined}
      />
      {isAdmin && <input {...getInputProps()} style={{ display: 'none' }} />}
    </Box>
  );
};
